import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import TagManager from 'react-gtm-module';
import { useAtomValue } from 'jotai';
import { MyInfoState } from '@/recoil/atom';
import { MyInfo } from '@/types/api';
import { useDebounce } from 'usehooks-ts';

export function InitializeGTM() {
  const myInfo = useAtomValue(MyInfoState);
  const { events } = useRouter();

  // 환경변수
  const gtmId = process.env.NEXT_PUBLIC_GTM_ID;
  const gtmAuth = process.env.NEXT_PUBLIC_GTM_AUTH;
  const gtmPreview = process.env.NEXT_PUBLIC_GTM_PREVIEW;

  const isInitialized = useRef(false);

  const prevMyInfo = useRef<MyInfo | null>(null);
  const debouncedMyInfo = useDebounce(myInfo, 500);

  // GTM 초기화 및 PageView 설정
  useEffect(() => {
    if (!gtmId || isInitialized.current) return;

    TagManager.initialize({ gtmId, auth: gtmAuth, preview: gtmPreview });
    isInitialized.current = true;

    const handleRouteChange = (url: string) => {
      TagManager.dataLayer({ dataLayer: { event: 'page_view', page: url } });
    };

    handleRouteChange(window.location.pathname);

    events.on('routeChangeComplete', handleRouteChange);
    return () => {
      events.off('routeChangeComplete', handleRouteChange);
    };
  }, [gtmId, events]);

  // User 정보 업데이트
  useEffect(() => {
    // 초기화 안 됐거나, 데이터가 없으면 패스
    if (!isInitialized.current || !debouncedMyInfo) return;

    // 비교 대상을 'debouncedMyInfo'로 통일 (일관성 확보)
    if (JSON.stringify(prevMyInfo.current) === JSON.stringify(debouncedMyInfo))
      return;

    // null safety 적용 (?. 사용)
    const isLoggedIn =
      !!debouncedMyInfo?.positionName || !!debouncedMyInfo?.departmentName;

    // TagManager 유틸리티 사용 (window 직접 접근 지양)
    TagManager.dataLayer({
      dataLayer: {
        event: 'userInfoUpdate',
        // user_id는 최상위에 위치 (GA4 식별용)
        user_id: debouncedMyInfo.portalUserKey || null,

        userInfo: {
          isLoggedIn,
          user_employeeNumber: debouncedMyInfo.employeeNumber || null,
          user_grade: debouncedMyInfo.positionName || null,
          user_team: debouncedMyInfo.departmentName || null,
        },
      },
    });

    // 현재 보낸 데이터를 ref에 저장
    prevMyInfo.current = debouncedMyInfo;
  }, [debouncedMyInfo]);

  return null;
}
